/**
 * Application chrome: the bot switcher, the tab bar and the user menu.
 *
 * The tab set mirrors LiveChatAI's so staff moving across from it recognise the
 * layout. Tabs whose page is not built yet are still shown, marked "soon", so
 * the navigation does not change shape as later milestones land.
 */

import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '../hooks/useAuth';
import { ALL_SITES, useBots } from '../hooks/useBots';
import { botLabel } from '../lib/format';
import { Card, Spinner } from './ui';

interface Tab {
  to: string;
  label: string;
  ready: boolean;
}

const OVERVIEW_TAB: Tab = { to: '/overview', label: 'Overview', ready: true };

/** Deployment-wide and admin only; shown last whichever site is selected. */
const ADMIN_TABS: Tab[] = [
  { to: '/ai-provider', label: 'AI Provider', ready: true },
  { to: '/email', label: 'Email', ready: true },
  { to: '/system-check', label: 'System check', ready: true },
  { to: '/latency', label: 'Latency', ready: true },
];

/**
 * Pages whose changes are admin-only on the server. A viewer can read them and
 * still answer customers in Chat Inbox; saying so up front beats a 403 on the
 * first button they press.
 */
const ADMIN_EDITS = new Set([
  '/data-sources',
  '/suggestions',
  '/settings',
  '/contacts',
  '/reports',
]);

const TABS: Tab[] = [
  { to: '/preview', label: 'Preview', ready: true },
  { to: '/data-sources', label: 'Data Sources', ready: true },
  { to: '/suggestions', label: 'AI Suggestions', ready: true },
  { to: '/settings', label: 'Settings', ready: true },
  { to: '/inbox', label: 'Chat Inbox', ready: true },
  { to: '/contacts', label: 'Contacts', ready: true },
  { to: '/reports', label: 'Reports', ready: true },
  { to: '/embed', label: 'Embed & Integrate', ready: true },
  { to: '/analytics', label: 'Analytics', ready: true },
];

export function Layout(): JSX.Element {
  const { user, logout } = useAuth();
  const { bots, selected, allSelected, select, loading } = useBots();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const onOverview = pathname === OVERVIEW_TAB.to;
  // None of these pages belongs to one bot, so "All websites" does not hide
  // them behind the site picker.
  const siteIndependent = onOverview || ADMIN_TABS.some((tab) => tab.to === pathname);
  const tabs = [
    ...(allSelected ? [OVERVIEW_TAB] : []),
    ...TABS,
    ...(user?.role === 'admin' ? ADMIN_TABS : []),
  ];

  function handleSwitch(next: string): void {
    select(next);
    // The overview only exists across sites, and one site's analytics is the
    // closest thing to it, so each choice lands somewhere that makes sense.
    if (next === ALL_SITES) navigate(OVERVIEW_TAB.to);
    else if (onOverview) navigate('/analytics');
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-brand-navy text-white">
        <div className="mx-auto flex max-w-7xl items-center gap-4 px-4 py-3">
          <span className="text-lg font-semibold tracking-tight">FormaChat</span>

          <label className="sr-only" htmlFor="bot-switcher">
            Select bot
          </label>
          <select
            id="bot-switcher"
            value={allSelected ? ALL_SITES : (selected?.slug ?? '')}
            onChange={(event) => handleSwitch(event.target.value)}
            disabled={loading || bots.length === 0}
            className="rounded-md border border-white/20 bg-white/10 px-3 py-1.5 text-sm text-white outline-none focus:border-white/50"
          >
            {bots.length > 1 && (
              <option value={ALL_SITES} className="text-slate-900">
                All websites
              </option>
            )}
            {bots.map((bot) => (
              <option key={bot.slug} value={bot.slug} className="text-slate-900">
                {botLabel(bot)}
              </option>
            ))}
          </select>

          <div className="ml-auto flex items-center gap-3 text-sm">
            <span className="text-white/70">{user?.email}</span>
            <button
              type="button"
              onClick={logout}
              className="rounded-md border border-white/20 px-3 py-1.5 hover:bg-white/10"
            >
              Sign out
            </button>
          </div>
        </div>

        <nav className="mx-auto max-w-7xl px-4">
          <ul className="flex flex-wrap gap-1">
            {tabs.map((tab) => (
              <li key={tab.to}>
                <NavLink
                  to={tab.to}
                  className={({ isActive }) =>
                    `inline-block border-b-2 px-3 py-2 text-sm transition ${
                      isActive
                        ? 'border-white font-medium text-white'
                        : 'border-transparent text-white/70 hover:text-white'
                    }`
                  }
                >
                  {tab.label}
                  {!tab.ready && (
                    <span className="ml-1.5 rounded bg-white/15 px-1 text-[10px] uppercase tracking-wide">
                      soon
                    </span>
                  )}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-6">
        {loading ? (
          <Spinner label="Loading bots…" />
        ) : allSelected && !siteIndependent ? (
          <SitePicker onPick={handleSwitch} />
        ) : (
          <>
            {user?.role !== 'admin' && ADMIN_EDITS.has(pathname) && (
              <div className="mb-4 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
                View only. Changes on this page need an administrator.
              </div>
            )}
            <Outlet />
          </>
        )}
      </main>
    </div>
  );
}

/**
 * Shown on a site-specific tab while "All websites" is chosen. These pages edit
 * or list one bot's data, so rather than guess which, ask.
 */
function SitePicker({ onPick }: { onPick: (slug: string) => void }): JSX.Element {
  const { bots } = useBots();
  const navigate = useNavigate();

  return (
    <Card className="mx-auto max-w-lg p-6 text-center">
      <p className="text-sm font-medium text-slate-700">
        This page works on one website at a time.
      </p>
      <p className="mt-1 text-sm text-slate-500">Choose which one to open:</p>
      <div className="mt-4 flex flex-col gap-2">
        {bots.map((bot) => (
          <button
            key={bot.slug}
            type="button"
            onClick={() => onPick(bot.slug)}
            className="rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:border-brand-blue hover:bg-brand-light"
          >
            {botLabel(bot)}
          </button>
        ))}
      </div>
      <button
        type="button"
        onClick={() => navigate(OVERVIEW_TAB.to)}
        className="mt-4 text-xs text-slate-500 underline hover:text-slate-700"
      >
        Back to the overview of all websites
      </button>
    </Card>
  );
}
